import { ACOUSTIC_DEFAULTS } from './config';
import type { ScoredSituation, StateMachineResult } from './types';
import { clamp01 } from './utils';

type StateMachineOptions = {
  frameDurationSec: number;
  holdSeconds?: number;
  hysteresisMargin?: number;
  ambiguityThreshold?: number;
};

export class SituationStateMachine {
  private currentState: StateMachineResult['state'] = 'unclear';

  private holdProgressSec = 0;

  private candidateState: ScoredSituation['key'] | null = null;

  private candidateDurationSec = 0;

  private readonly holdSeconds: number;

  private readonly hysteresisMargin: number;

  private readonly ambiguityThreshold: number;

  private frameDurationSec: number;

  constructor(options: StateMachineOptions) {
    this.frameDurationSec = options.frameDurationSec;
    this.holdSeconds = options.holdSeconds ?? ACOUSTIC_DEFAULTS.stateHoldSeconds;
    this.hysteresisMargin = options.hysteresisMargin ?? ACOUSTIC_DEFAULTS.stateHysteresisMargin;
    this.ambiguityThreshold = options.ambiguityThreshold ?? ACOUSTIC_DEFAULTS.transitionAmbiguityThreshold;
  }

  configureFrameDuration(frameDurationSec: number): void {
    if (frameDurationSec > 0) this.frameDurationSec = frameDurationSec;
  }

  reset(): void {
    this.currentState = 'unclear';
    this.holdProgressSec = 0;
    this.candidateState = null;
    this.candidateDurationSec = 0;
  }

  update(sortedScores: ScoredSituation[]): StateMachineResult {
    const top = sortedScores[0];
    const second = sortedScores[1];
    if (!top) {
      this.currentState = 'unclear';
      this.holdProgressSec = 0;
      return {
        state: 'unclear',
        persistence: 0,
        transitionLikelihood: 1
      };
    }

    const ambiguity = second ? clamp01(1 - (top.score - second.score)) : 0.2;
    const shouldTransitionState = ambiguity > 1 - this.ambiguityThreshold;

    if (top.score < 0.33) {
      this.currentState = 'unclear';
      this.holdProgressSec = 0;
      return {
        state: 'unclear',
        persistence: 0,
        transitionLikelihood: clamp01(ambiguity + 0.2)
      };
    }

    if (this.currentState === top.key) {
      this.holdProgressSec += this.frameDurationSec;
      this.candidateState = null;
      this.candidateDurationSec = 0;
      return {
        state: this.currentState,
        persistence: clamp01(this.holdProgressSec / this.holdSeconds),
        transitionLikelihood: clamp01(ambiguity * 0.7)
      };
    }

    const currentScore = sortedScores.find((entry) => entry.key === this.currentState)?.score ?? 0;
    const canOvertake = top.score >= currentScore + this.hysteresisMargin;

    if (top.key !== this.candidateState) {
      this.candidateState = top.key;
      this.candidateDurationSec = this.frameDurationSec;
    } else {
      this.candidateDurationSec += this.frameDurationSec;
    }

    if (canOvertake) {
      if (this.candidateDurationSec >= this.holdSeconds) {
        this.currentState = top.key;
        this.holdProgressSec = this.candidateDurationSec;
        this.candidateState = null;
        this.candidateDurationSec = 0;
      }
    } else {
      this.candidateState = null;
      this.candidateDurationSec = 0;
    }

    const fallbackState = shouldTransitionState ? 'unclear' : this.currentState;
    return {
      state: fallbackState,
      persistence: clamp01(this.holdProgressSec / this.holdSeconds),
      transitionLikelihood: clamp01(ambiguity + (canOvertake ? 0.2 : 0))
    };
  }
}
